import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  TextField,
  MenuItem,
  Typography,
  Box,
} from '@mui/material';
import api from '../utils/api';
import { useSnackbar } from 'notistack';

interface Approver {
  id: string;
  name: string;
  role: string;
  hasPin: boolean;
}

interface RefundPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Refund mode: the Stripe payment to refund. */
  payment: {
    id: string;
    amount: number; // dollars (balance-endpoint row shape)
    paymentMethod?: string | null;
    paymentType?: string | null;
  } | null;
  /** Void mode: void a manual payment or a whole invoice instead. */
  voidTarget?: { kind: 'payment' | 'invoice'; id: string; label: string } | null;
}

/**
 * Maker-checker approval dialog for ALL money reversals: in-app Stripe
 * refunds AND voids of payments/invoices (any tender type — cash and check
 * included). The approver can be someone other than the logged-in user
 * (shared front-desk sessions) — the PIN is what attributes the approval in
 * the audit trail. PIN verification, lockout, and the action itself all
 * happen server-side.
 */
const RefundPaymentDialog: FC<RefundPaymentDialogProps> = ({ open, onClose, onSuccess, payment, voidTarget }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [approvers, setApprovers] = useState<Approver[]>([]);
  const [approverId, setApproverId] = useState('');
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const isVoid = !!voidTarget;

  const isAch = (payment?.paymentMethod || payment?.paymentType || '').toLowerCase().includes('ach')
    || (payment?.paymentMethod || '').toLowerCase().includes('bank');

  useEffect(() => {
    if (!open) return;
    setError(null);
    setPin('');
    setReason('');
    api
      .get('/users/refund-approvers')
      .then((res) => {
        const list: Approver[] = res.data || [];
        setApprovers(list);
        // Preselect when there's exactly one admin with a PIN set.
        const withPin = list.filter((a) => a.hasPin);
        setApproverId(withPin.length === 1 ? withPin[0].id : '');
      })
      .catch(() => setApprovers([]));
  }, [open]);

  const submit = async () => {
    setProcessing(true);
    setError(null);
    try {
      if (voidTarget) {
        if (voidTarget.kind === 'payment') {
          await api.post(`/payments/${voidTarget.id}/void`, {
            approverId,
            pin,
            reason: reason.trim() || undefined,
          });
          enqueueSnackbar('Payment voided — balance restored.', { variant: 'success' });
        } else {
          await api.post(`/billing/${voidTarget.id}/void`, {
            approverId,
            pin,
            comment: reason.trim() || undefined,
          });
          enqueueSnackbar('Invoice voided.', { variant: 'success' });
        }
      } else {
        if (!payment) return;
        await api.post(`/payments/${payment.id}/refund`, {
          approverId,
          pin,
          reason: reason.trim() || undefined,
        });
        enqueueSnackbar(
          isAch
            ? 'Refund initiated — bank refunds take a few business days.'
            : 'Refund initiated — it will show as refunded once Stripe confirms.',
          { variant: 'success' },
        );
      }
      onSuccess?.();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || (isVoid ? 'Void failed' : 'Refund failed'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !processing && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>
        {isVoid
          ? `Void ${voidTarget!.label}?`
          : `Refund payment${payment ? ` of $${payment.amount.toFixed(2)}` : ''}?`}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isVoid
            ? voidTarget!.kind === 'payment'
              ? 'The payment will be marked VOIDED and the invoice balance will become due again.'
              : 'The invoice will be marked VOIDED (cancelled). Kept for audit.'
            : (
              <>
                The client gets the full amount back. Stripe keeps the original processing fee, so the
                clinic absorbs it.{' '}
                {isAch
                  ? 'Bank refunds take a few business days to reach the client.'
                  : 'Card refunds usually appear within a few days on the client’s statement.'}
              </>
            )}
        </Typography>

        <TextField
          select
          fullWidth
          size="small"
          label="Approved by"
          value={approverId}
          onChange={(e) => setApproverId(e.target.value)}
          sx={{ mb: 2 }}
          helperText={`An admin or owner approves ${isVoid ? 'voids' : 'refunds'} with their PIN.`}
        >
          {approvers.length === 0 && (
            <MenuItem disabled value="">
              <em>No admins found</em>
            </MenuItem>
          )}
          {approvers.map((a) => (
            <MenuItem key={a.id} value={a.id} disabled={!a.hasPin}>
              {a.name}
              {!a.hasPin && ' — no PIN set'}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          size="small"
          type="password"
          label="Approval PIN"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, autoComplete: 'one-time-code' }}
          sx={{ mb: 2 }}
          helperText="6 digits. Set yours in Settings → Account settings."
        />

        <TextField
          fullWidth
          size="small"
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          inputProps={{ maxLength: 500 }}
          placeholder={isVoid ? 'e.g. incorrect entry, duplicate invoice' : undefined}
        />

        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            The {isVoid ? 'void' : 'refund'} is logged with who requested and who approved it.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={processing}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={submit}
          disabled={processing || !approverId || pin.length !== 6}
        >
          {processing
            ? (isVoid ? 'Voiding…' : 'Refunding…')
            : (isVoid ? 'Void' : 'Refund')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundPaymentDialog;
